import React, { useState } from 'react';
import { Suspect } from '../../types/crime-game';
import { User } from 'lucide-react';

interface GuessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedSuspect: Suspect | null;
  suspects: Suspect[];
  onSelectSuspect: (suspect: Suspect) => void;
  answeredCount: number;
  totalQuestions: number;
  revealedCount: number;
}

export const GuessModal: React.FC<GuessModalProps> = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  selectedSuspect,
  suspects,
  onSelectSuspect,
  answeredCount,
  totalQuestions,
  revealedCount
}) => {
  const [tempSelected, setTempSelected] = useState<Suspect | null>(selectedSuspect);

  React.useEffect(() => {
    if (isOpen) {
      setTempSelected(selectedSuspect);
    }
  }, [isOpen, selectedSuspect]);

  if (!isOpen) return null;

  const handleSelect = (suspect: Suspect) => {
    setTempSelected(suspect);
    onSelectSuspect(suspect);
  };

  const handleConfirm = () => {
    if (tempSelected) {
      onConfirm();
    }
  };

  const hasStartedInvestigating = answeredCount > 0 || revealedCount > 0;
  const hasFinishedInvestigating = answeredCount >= totalQuestions;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-scale-in max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Final Accusation</h3>

        <div className="mb-6">
          {!hasStartedInvestigating ? (
            <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-r-lg text-sm text-red-800">
              <strong>Warning:</strong> You haven't answered any questions or revealed any clues! It's highly recommended to gather evidence before making an accusation.
            </div>
          ) : !hasFinishedInvestigating ? (
            <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-r-lg text-sm text-amber-800">
              <strong>Note:</strong> You have gathered some evidence, but there are still unanswered questions. You might want to gather more clues to be absolutely sure!
            </div>
          ) : null}
        </div>

        {!tempSelected ? (
          <div className="mb-6">
            <p className="text-gray-600 mb-4 text-center">Select a suspect to accuse:</p>
            <div className="space-y-3">
              {suspects.map((suspect) => (
                <button
                  key={suspect.id}
                  onClick={() => handleSelect(suspect)}
                  className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-all text-left"
                >
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gray-100 rounded-full">
                      <User className="w-6 h-6 text-gray-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800">{suspect.name}</h4>
                      <p className="text-sm text-gray-600">{suspect.bio}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center mb-6">
            <p className="text-gray-600 mb-2">You are about to accuse:</p>
            <div className="text-xl font-bold text-indigo-600 p-3 bg-indigo-50 rounded-lg inline-block">
              {tempSelected.name}
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Are you sure? This cannot be undone.
            </p>
            <button
              onClick={() => setTempSelected(null)}
              className="mt-2 text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Change selection
            </button>
          </div>
        )}

        <div className="flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!tempSelected}
            className={`
              flex-1 px-4 py-2 rounded-lg text-white font-bold shadow-md
              ${tempSelected
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-gray-300 cursor-not-allowed'}
            `}
          >
            Confirm Accusation
          </button>
        </div>
      </div>
    </div>
  );
};
